/* eslint-disable no-console */
/**
 * Modal de override de clasificación PER-FACTURA (Fase O-2 — solo SII recibidas).
 *
 * Distinto del "Clasificar proveedor": acá se marca UNA factura puntual como excepción CONTRA
 * la regla del proveedor (proveedor Gasto → esta factura Activo, y viceversa). Se abre desde el
 * chip de Clasificación de la fila (clic con stopPropagation); la fila entera sigue abriendo el
 * modal por-proveedor.
 *
 * "Aplicar excepción" escribe vía `clasificarFacturaUnica` (que ya setea clasificacion_manual =
 * true, O-1): ningún automatismo la pisa después. "Volver a la regla del proveedor" quita el
 * override vía `volverAReglaDelProveedor` (re-aplica la regla con manual = false, o la deja sin
 * clasificar si el proveedor no tiene regla). Solo el eje de clasificación; nada del de pago.
 */
import { useState } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { MODO, clasificarFacturaUnica, volverAReglaDelProveedor } from "../../utils/compras/clasificacionProveedor";

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));
const labelCuenta = (v) => CUENTAS_ER.find((c) => c.value === v)?.label || v;

const selectStyle = {
  width: "100%",
  height: 36,
  fontSize: "0.82rem",
  padding: "4px 10px",
  border: "1px solid #ced4da",
  borderRadius: 4,
};

const ClasificarFacturaModal = ({ doc, onClose, onAplicada }) => {
  const {
    id: facturaId,
    folio,
    proveedorNombre,
    proveedorId,
    cuentaEr,
    tipoContable,
    clasificacionManual,
    razonSocial,
  } = doc;

  // Prefill desde la clasificación vigente de la factura.
  const [modo, setModo] = useState(tipoContable === "activo" ? MODO.ACTIVO : MODO.GASTO);
  const [cuentaER, setCuentaER] = useState(tipoContable === "activo" ? "" : cuentaEr || "");
  const [guardando, setGuardando] = useState(false);

  // Descripción del estado actual y su origen.
  const origen = clasificacionManual
    ? "excepción manual"
    : tipoContable === "activo" || cuentaEr
      ? "regla del proveedor"
      : "sin clasificar";
  const vigente =
    tipoContable === "activo" ? "Activo" : cuentaEr ? `Gasto → ${labelCuenta(cuentaEr)}` : "Sin clasificar";

  const aplicar = async () => {
    if (modo === MODO.GASTO && !cuentaER) return toast.error("Elegí una cuenta del ER para el gasto");
    setGuardando(true);
    try {
      await clasificarFacturaUnica({ facturaId, modo, cuentaEr: modo === MODO.GASTO ? cuentaER : null });
      toast.success("Excepción aplicada a esta factura");
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarFacturaModal: error al aplicar excepción", err);
      toast.error("No se pudo aplicar la excepción");
    } finally {
      setGuardando(false);
    }
  };

  const volverALaRegla = async () => {
    setGuardando(true);
    try {
      await volverAReglaDelProveedor({ facturaId, proveedorId });
      toast.success(
        proveedorId ? "Factura devuelta a la regla del proveedor" : "Excepción quitada (queda sin clasificar)"
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarFacturaModal: error al volver a la regla", err);
      toast.error("No se pudo quitar la excepción");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>
        Clasificar factura — Folio {folio} &middot; {proveedorNombre}
      </ModalHeader>
      <ModalBody>
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 12px",
            borderRadius: 6,
            fontSize: "0.78rem",
            marginBottom: 14,
          }}
        >
          Clasificación vigente: <strong>{vigente}</strong> <span style={{ color: "#909497" }}>({origen})</span>
          {razonSocial && razonSocial !== proveedorNombre && (
            <div style={{ color: "#909497", marginTop: 4 }}>Razón social SII: {razonSocial}</div>
          )}
        </div>

        <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 12 }}>
          La excepción vale <strong>solo para esta factura</strong> y no la pisa ningún automatismo. La regla del
          proveedor sigue gobernando al resto.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Clasificación de esta factura
          </label>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="modoFactura"
                value={MODO.GASTO}
                checked={modo === MODO.GASTO}
                onChange={() => setModo(MODO.GASTO)}
                style={{ marginRight: 4 }}
              />
              Gasto
            </label>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="modoFactura"
                value={MODO.ACTIVO}
                checked={modo === MODO.ACTIVO}
                onChange={() => setModo(MODO.ACTIVO)}
                style={{ marginRight: 4 }}
              />
              Activo
            </label>
          </div>
        </div>

        {modo === MODO.GASTO ? (
          <div style={{ marginBottom: 4 }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Cuenta del Estado de Resultados
            </label>
            <select value={cuentaER} onChange={(e) => setCuentaER(e.target.value)} style={selectStyle}>
              <option value="">— Seleccionar cuenta —</option>
              {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
                <optgroup key={grupo} label={grupo}>
                  {cuentas.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ) : (
          <div
            style={{
              background: "#eaf2f8",
              color: "#2874a6",
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: "0.78rem",
              marginBottom: 4,
            }}
          >
            Se marca como <strong>activo</strong> y sale de la línea de costo del ER. El alta / depreciación del activo
            se hace aparte.
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ justifyContent: "space-between" }}>
        <span>
          {clasificacionManual && (
            <Button
              color="link"
              size="sm"
              style={{ color: "#c0392b", padding: 0 }}
              onClick={volverALaRegla}
              disabled={guardando}
            >
              Volver a la regla del proveedor
            </Button>
          )}
        </span>
        <span>
          <Button color="secondary" outline size="sm" onClick={onClose} disabled={guardando} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
          <Button color="success" size="sm" style={{ minWidth: 120 }} onClick={aplicar} disabled={guardando}>
            {guardando ? "Guardando…" : "Aplicar excepción"}
          </Button>
        </span>
      </ModalFooter>
    </Modal>
  );
};

export default ClasificarFacturaModal;
